<script setup lang="ts">
/** The Zod report grouped by owning node, each row focusing its chapter. Blockers (engineering must
 *  fix the stored row) are separated from fixable gaps. */
import type { AssessmentValidationBlocker } from '../../../model/assessments/interfaces'
import type { AssessmentValidationGroup } from '../../../model/assessments/interfaces'
import type { AssessmentValidationGroupIssue } from '../../../model/assessments/interfaces'

import { AppText } from '@shared/ui/typography'

defineOptions({
  name: 'AssignmentValidationReport',
})

withDefaults(defineProps<{
  hasValidated?: boolean
  summary?: string | null
  hasProblems?: boolean
  /** The list describes a document that has since been edited. */
  isStale?: boolean
  blockers?: AssessmentValidationBlocker[]
  groups?: AssessmentValidationGroup[]
}>(), {
  hasValidated: false,
  summary: null,
  hasProblems: false,
  isStale: false,
  blockers: () => [],
  groups: () => [],
})

const emit = defineEmits<{
  'click:issue': [issue: AssessmentValidationGroupIssue]
}>()
</script>

<template>
  <!-- Not gated on `summary` alone: a blocker-only document has no summary line. -->
  <div
    v-if="hasValidated && (summary || blockers.length || groups.length)"
    class="assignment-validation-report"
  >
    <AppText
      v-if="summary"
      name="body2-medium"
      :color="hasProblems ? 'red-600' : 'emerald-600'"
    >
      {{ summary }}
    </AppText>

    <!-- The list is kept after an edit rather than dropped, so an author can work through it — but
         it is labelled, because the document it describes is no longer the one on screen. -->
    <AppText
      v-if="isStale"
      name="body2"
      color="slate-500"
    >
      This report describes the assignment before your latest edit. Validate again to refresh it.
    </AppText>

    <!-- One banner for everything typing cannot fix, with the next step spelled out. -->
    <div
      v-if="blockers.length"
      class="assignment-validation-report__blockers"
    >
      <ul class="assignment-validation-report__blocker-list">
        <li
          v-for="blocker in blockers"
          :key="blocker.label"
        >
          <AppText name="body2-medium">
            {{ blocker.label }}
          </AppText>
          <AppText
            name="body2"
            color="slate-500"
          >
            {{ blocker.message }}
          </AppText>
        </li>
      </ul>
      <AppText
        name="body2"
        color="slate-500"
      >
        You can still read and edit the rest of the assignment. Ask engineering to fix the stored
        document before saving.
      </AppText>
    </div>

    <!-- The fixable half — each row selects the chapter it belongs to. -->
    <ul
      v-if="groups.length"
      class="assignment-validation-report__groups"
    >
      <li
        v-for="group in groups"
        :key="group.key"
        class="assignment-validation-report__group"
      >
        <AppText name="body2-medium">
          {{ group.label }}
        </AppText>
        <ul class="assignment-validation-report__issues">
          <li
            v-for="issue in group.issues"
            :key="`${issue.path}:${issue.message}`"
          >
            <button
              type="button"
              class="assignment-validation-report__issue"
              @click="emit('click:issue', issue)"
            >
              <AppText
                v-if="issue.field"
                name="body2-medium"
                color="slate-500"
              >
                {{ issue.field }}
              </AppText>
              <AppText name="body2">
                {{ issue.message }}
              </AppText>
            </button>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss" src="./AssignmentValidationReport.scss" />
